/**
 * MODULE: domain/pages/frontend/hooks/usePageCommentTasks.ts
 * PURPOSE: Lookup and create CRM tasks linked to page comments
 */

import { useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { taskApi, type Task } from '@shared/lib/frontend/api/taskApi';
import type { PageComment } from '../../contracts/pages.types';
import {
  buildCommentToTaskRequest,
  extractCommentIdFromTaskTags,
} from '../utils/pageCommentTaskUtils';
import { taskHasPageSyncTag } from '../utils/pageTasksSyncUtils';
import { PAGE_TASKS_SYNC_KEY } from './usePageTasksSync';

export const PAGE_COMMENT_TASKS_KEY = (pageId: string) => ['pages', pageId, 'comment-tasks'] as const;

export function usePageCommentTasks(pageId: string, enabled = true) {
  const query = useQuery({
    queryKey: PAGE_COMMENT_TASKS_KEY(pageId),
    queryFn: () => taskApi.list({ limit: 200 }),
    enabled: Boolean(pageId) && enabled,
    staleTime: 30_000,
  });

  const taskByCommentId = useMemo(() => {
    const map = new Map<string, Task>();
    for (const task of query.data ?? []) {
      if (!taskHasPageSyncTag(task, pageId)) {
        continue;
      }
      const commentId = extractCommentIdFromTaskTags(task.tags);
      if (commentId) {
        map.set(commentId, task);
      }
    }
    return map;
  }, [pageId, query.data]);

  return {
    taskByCommentId,
    isLoading: query.isLoading,
    refetch: query.refetch,
  };
}

export function useConvertCommentToTask(pageId: string, workspaceId: string) {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: (comment: PageComment) =>
      taskApi.create(buildCommentToTaskRequest(pageId, workspaceId, comment)),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: PAGE_COMMENT_TASKS_KEY(pageId) });
      qc.invalidateQueries({ queryKey: PAGE_TASKS_SYNC_KEY(pageId) });
    },
  });
}
